import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutomobileDataEntity } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/entities/AutomobileDataEntity';
import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import { useErrorBoundary } from '@/components/shared/components/ErrorBoundary/hooks/useErrorBoundary';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

export const useAutomobileForm = () => {
  const {
    state: { automobileData, selectedMerchant },
    actions: { updateApplicationContext },
  } = useApplicationContext();

  const { watch } = useFormContext();
  const { throwError } = useErrorBoundary();
  const [isDataInitialized, setIsDataInitialized] = useState(false);

  const selectedBrand = watch('item.brand');
  const selectedModel = watch('item.model');
  const selectedVersion = watch('item.version');

  const updateAutomobileData = async (cartItem: AutomobileFormItem) => {
    const currentCartItem = cartItem as AutomobileFormItem;
    if (
      automobileData?.selectedBrand?.value === currentCartItem.brand &&
      automobileData?.selectedModel?.value === currentCartItem.model &&
      automobileData?.selectedVersion?.value === currentCartItem.version
    ) {
      return;
    }
    if (!cartItem || !automobileData) return;

    await updateSelectedBrand(currentCartItem.brand, automobileData);
    await updateSelectedModel(currentCartItem.model, automobileData);
    await updateSelectedVersion(currentCartItem.version, automobileData);
  };

  const updateAllData = async () => {
    if (!automobileData) return;
    try {
      if (selectedBrand && automobileData.selectedBrand?.value !== selectedBrand) {
        await updateSelectedBrand(selectedBrand, automobileData);
      }
      if (selectedModel && automobileData.selectedModel?.value !== selectedModel) {
        await updateSelectedModel(selectedModel, automobileData);
      }
      if (selectedVersion && automobileData.selectedVersion?.value !== selectedVersion) {
        await updateSelectedVersion(selectedVersion, automobileData);
      }
      setIsDataInitialized(true);
    } catch (err) {
      throwError(err);
    }
  };

  const createAutomobileDataEntities = async (
    selectedBrand: string,
    selectedModel: string,
    selectedVersion: string,
  ): Promise<void> => {
    if (automobileData || !selectedMerchant) return;

    try {
      const newAutomobileData = new AutomobileDataEntity(selectedMerchant.id);
      await newAutomobileData.init(selectedBrand, selectedModel, selectedVersion);
      updateApplicationContext({ automobileData: newAutomobileData });
      setIsDataInitialized(true);
    } catch (err) {
      throwError(err);
    }
  };

  const updateSelectedBrand = async (selectedBrand: string, automobileData: AutomobileDataEntity): Promise<void> => {
    try {
      const newAutomobileData = await automobileData.updateBrand(selectedBrand);
      updateApplicationContext({ automobileData: newAutomobileData });
    } catch (err) {
      throwError(err);
    }
  };

  const updateSelectedModel = async (selectedModel: string, automobileData: AutomobileDataEntity): Promise<void> => {
    try {
      const newAutomobileData = await automobileData.updateModel(selectedModel);
      updateApplicationContext({ automobileData: newAutomobileData });
    } catch (err) {
      throwError(err);
    }
  };

  const updateSelectedVersion = async (
    selectedVersion: string,
    automobileData: AutomobileDataEntity,
  ): Promise<AutomobileDataEntity | undefined> => {
    try {
      const newAutomobileData = await automobileData.updateVersion(selectedVersion);
      updateApplicationContext({ automobileData: newAutomobileData });
      return newAutomobileData;
    } catch (err) {
      throwError(err);
    }
  };

  return {
    automobileData,
    isDataInitialized,
    updateAutomobileData,
    createAutomobileDataEntities,
    updateAllData,
    updateSelectedBrand,
    updateSelectedModel,
    updateSelectedVersion,
  };
};
